/**
 * Live input masks and checks for contact-style fields, shared by the client drawer's
 * Demographics (#5 phone, #3d SSN, #9 validation) and Settings → Locations (ehr#302).
 */

/** "(555) 555-5555" while typing; 10 digits at most. */
export function maskPhone(v: string): string {
  const d = v.replace(/\D/g, '').slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/** "555-55-5555" while typing; 9 digits at most. */
export function maskSsn(v: string): string {
  const d = v.replace(/\D/g, '').slice(0, 9);
  if (d.length < 4) return d;
  if (d.length < 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
}

/**
 * The Demographics SSN passes the save check when empty, a complete 9-digit number, or the saved
 * value still masked (***-**-1234, ehr#712: the server keeps the stored SSN for it). A half-typed
 * SSN must be pointed out on save (#3).
 */
export function ssnFieldValid(v: string): boolean {
  if (/^\*{3}-\*{2}-[\d*]{4}$/.test(v)) return true;
  const d = v.replace(/\D/g, '');
  return d.length === 0 || d.length === 9;
}

/** True when the value is empty or a plausible email. */
export function emailValid(v: string): boolean {
  return !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

/** True when the value is empty or a complete 10-digit phone number. */
export function phoneValid(v: string): boolean {
  const d = v.replace(/\D/g, '');
  return d.length === 0 || d.length === 10;
}

/** ehr#356: digits only while typing, "12345" or ZIP+4 "12345-6789"; 9 digits at most. */
export function maskZip(v: string): string {
  const d = v.replace(/\D/g, '').slice(0, 9);
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
}

/** ehr#356: true when the value is empty, a 5-digit ZIP, or ZIP+4. */
export function zipValid(v: string): boolean {
  return !v || /^\d{5}(-\d{4})?$/.test(v.trim());
}
